import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Container,
  Grid,
  Stack,
  Typography,
  Box,
  Fade,
  Grow,
  alpha,
  useTheme,
  LinearProgress,
  IconButton,
  Paper,
  Snackbar,
  Alert,
  Autocomplete,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Tooltip,
} from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useApi } from '../../../hooks/useApi';
import { rendererFor } from './renderers';

// "ContractBalanceDatto" -> "Contract Balance Datto"
function humanize(s) {
  return (s || '').replace(/([a-z0-9])([A-Z])/g, '$1 $2').trim();
}

// "FINANCE-CONTRACT-BALANCE-DATTO" -> "Contract Balance Datto"
//
// The domain portion is intentionally dropped: the breadcrumb above this
// shows "Home / Audit Center / <name>" and the auditType slug is visible
// just under the header as code text, so repeating "Finance" here only
// added a second '/' that collided with the breadcrumb separator.
function prettyAuditType(slug) {
  if (!slug) return '';
  const parts = slug.split('-');
  if (parts.length < 2) return slug;
  const rest = parts.slice(1).join(' ');
  return rest.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

function FindingsShell() {
  const { auditType } = useParams();
  const [searchParams] = useSearchParams();
  const jApi = useApi();
  const theme = useTheme();
  const navigate = useNavigate();

  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [toast, setToast] = useState(null);

  // Filters
  const [selectedRunId, setSelectedRunId] = useState('');
  const [selectedAccount, setSelectedAccount] = useState(null);

  const fetchRecords = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const response = await jApi.request(
        'GET',
        `cma/audit?auditType=${encodeURIComponent(auditType)}`
      );
      const data = Array.isArray(response.data) ? response.data : [];
      setRecords(data);
    } catch (ex) {
      console.error('Failed to load findings:', ex);
      setErrorMessage(ex?.toString?.() || 'Failed to load findings');
      setRecords([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (auditType) fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditType]);

  // Build run dropdown from the data — distinct auditRunIds, sorted descending
  // by auditDate so the latest run is first. Default selection is the latest.
  const runOptions = useMemo(() => {
    const byRun = new Map();
    for (const r of records) {
      const id = r.auditRunId;
      if (!id) continue;
      const existing = byRun.get(id);
      if (!existing || (r.auditDate || '') > (existing.auditDate || '')) {
        byRun.set(id, { id, auditDate: r.auditDate || '' });
      }
    }
    const list = Array.from(byRun.values());
    list.sort((a, b) => (b.auditDate || '').localeCompare(a.auditDate || ''));
    return list;
  }, [records]);

  useEffect(() => {
    // Auto-select most recent run when data first loads, or when the user's
    // current pick disappears from the dropdown after a refresh.
    if (runOptions.length === 0) {
      setSelectedRunId('');
      return;
    }
    const stillPresent = runOptions.some((r) => r.id === selectedRunId);
    if (!stillPresent) {
      // Honor ?run=… in the URL on first load.
      const urlRun = searchParams.get('run');
      const target = urlRun && runOptions.some((r) => r.id === urlRun) ? urlRun : runOptions[0].id;
      setSelectedRunId(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runOptions]);

  // Distinct accounts among the records of the selected run.
  const accountOptions = useMemo(() => {
    const codes = new Set();
    for (const r of records) {
      if (!selectedRunId || r.auditRunId === selectedRunId) {
        if (r.account) codes.add(r.account);
      }
    }
    return Array.from(codes).sort();
  }, [records, selectedRunId]);

  // Filtered records the renderer actually sees.
  const visibleRecords = useMemo(() => {
    return records.filter((r) => {
      if (selectedRunId && r.auditRunId !== selectedRunId) return false;
      if (selectedAccount && r.account !== selectedAccount) return false;
      return true;
    });
  }, [records, selectedRunId, selectedAccount]);

  const Renderer = rendererFor(auditType);

  // In-flight requeue tracking. Keyed by record id; each entry carries the
  // ves queue id we got back from POST /ves/job and the latest status seen
  // from polling. The renderer reads this to show a spinner in place of the
  // Rerun action on the row whose record is currently queued/running.
  //
  // Both state and ref are kept in sync — state drives renders, ref lets the
  // polling tick read the latest set without having to re-create the
  // interval every time a status changes.
  const [inFlight, setInFlightState] = useState({});  // { [recordId]: { queueId, status } }
  const inFlightRef = useRef({});
  const setInFlight = (updater) => {
    setInFlightState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      inFlightRef.current = next;
      return next;
    });
  };

  // Per-record requeue: build a fresh queue item that targets the same
  // cma.audit doc (deterministic id). On success the Test- runs again, the
  // Pending state appears, then the verdict updates in place. The shell
  // tracks the queue item id so the renderer can surface a "queued/running"
  // affordance until it lands.
  const handleRequeue = async (record) => {
    if (!record || !record.functionPath) {
      setToast({ severity: 'error', text: 'Record is missing functionPath — cannot requeue' });
      return;
    }
    try {
      const resp = await jApi.request('POST', 'ves/job', {
        functionpath: record.functionPath,
        splat: {
          Account:    record.account,
          AuditId:    record.id,
          AuditRunId: record.auditRunId,
          AuditDate:  record.auditDate,
        },
      });
      const queueId = resp?.data?.id;
      if (queueId) {
        setInFlight((prev) => ({
          ...prev,
          [record.id]: { queueId, status: 'queued' },
        }));
      }
      setToast({ severity: 'success', text: `Re-queued ${record.account}` });
    } catch (ex) {
      console.error('Requeue failed:', ex);
      setToast({ severity: 'error', text: ex?.toString?.() || `Re-queue failed for ${record.account}` });
    }
  };

  // Polling: every 5 seconds while anything's in-flight, GET each tracked
  // queue item, advance its status, and on completion drop it from the set
  // and refresh the underlying records so the new verdict shows up.
  const hasInFlight = Object.keys(inFlight).length > 0;
  useEffect(() => {
    if (!hasInFlight) return undefined;

    const tick = async () => {
      const current = inFlightRef.current;
      const ids = Object.keys(current);
      if (ids.length === 0) return;

      const results = await Promise.all(ids.map(async (rid) => {
        const entry = current[rid];
        if (!entry?.queueId) return [rid, entry, false];
        try {
          const resp = await jApi.request('GET', `ves/job?id=${entry.queueId}`);
          const status = resp?.data?.status;
          const done = status === 'completed' || status === 'error';
          return [rid, done ? null : { ...entry, status: status || entry.status }, done];
        } catch (ex) {
          // Transient poll failure — leave the entry alone, retry next tick.
          return [rid, entry, false];
        }
      }));

      let anyDone = false;
      const next = {};
      for (const [rid, newEntry, done] of results) {
        if (newEntry !== null) next[rid] = newEntry;
        else anyDone = true;
      }
      setInFlight(next);
      if (anyDone) fetchRecords();
    };

    const intervalId = setInterval(tick, 5000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInFlight]);

  const headerGradient = {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: theme.palette.mode === 'dark'
      ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
      : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
  };

  return (
    // pb leaves clearance for the global fixed VersionFooter (position:fixed
    // at bottom). Without it the MaterialReactTable's pagination row gets
    // hidden behind the footer on long lists.
    <Box sx={{ minHeight: '100vh', pb: 6 }}>
      <Container maxWidth="xl">
        {/* Breadcrumb */}
        <Fade in timeout={500}>
          <Box sx={{ pt: 3, pb: 2 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.6),
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1.5,
                  '&:hover': { background: alpha(theme.palette.secondary.main, 0.1) } }}>
                  <HomeRoundedIcon sx={{ fontSize: 20, color: theme.palette.secondary.main }} />
                  <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>
                    Home
                  </Typography>
                </Box>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Link to="/audit" style={{ textDecoration: 'none' }}>
                <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600, px: 1, py: 0.5,
                  '&:hover': { color: theme.palette.secondary.dark } }}>
                  Audit Center
                </Typography>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 600, px: 1, py: 0.5 }}>
                {prettyAuditType(auditType)}
              </Typography>
            </Box>
          </Box>
        </Fade>

        <Grid container spacing={2}>
          {/* Header */}
          <Grid size={12}>
            <Grow in timeout={700}>
              <Box sx={headerGradient}>
                <Stack direction="row" spacing={2} sx={{ p: 2, alignItems: 'center', position: 'relative', zIndex: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      {prettyAuditType(auditType)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <code style={{ fontSize: '0.85em' }}>{auditType}</code>
                      {' · '}
                      {isLoading ? 'Loading…' :
                        `${records.length} record${records.length === 1 ? '' : 's'} total`}
                    </Typography>
                  </Box>
                  <Tooltip title="Refresh">
                    <span>
                      <IconButton onClick={fetchRecords} disabled={isLoading} size="small"
                        sx={{
                          color: theme.palette.secondary.main,
                          '&:hover': { background: alpha(theme.palette.secondary.main, 0.1) },
                        }}>
                        <RefreshIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                {isLoading && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }} />}
              </Box>
            </Grow>
          </Grid>

          {/* Filter row */}
          {!isLoading && records.length > 0 && (
            <Grid size={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  background: alpha(theme.palette.background.paper, 0.7),
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
                  borderRadius: 2,
                }}
              >
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 260 }}>
                    <InputLabel id="audit-run-label">Run</InputLabel>
                    <Select
                      labelId="audit-run-label"
                      label="Run"
                      value={selectedRunId}
                      onChange={(e) => setSelectedRunId(e.target.value)}
                    >
                      {runOptions.map((r) => (
                        <MenuItem key={r.id} value={r.id}>
                          {r.id}{r.auditDate ? `  (${r.auditDate})` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Autocomplete
                    size="small"
                    options={accountOptions}
                    value={selectedAccount}
                    onChange={(_, v) => setSelectedAccount(v)}
                    sx={{ minWidth: 220 }}
                    renderInput={(params) => (
                      <TextField {...params} label="Account" placeholder="All accounts" />
                    )}
                  />

                  <Box sx={{ flex: 1 }} />

                  <Chip
                    label={`Showing ${visibleRecords.length} of ${records.length}`}
                    size="small"
                    variant="outlined"
                  />
                </Stack>
              </Paper>
            </Grid>
          )}

          {/* Error banner */}
          {errorMessage && (
            <Grid size={12}>
              <Paper sx={{
                p: 2,
                background: alpha(theme.palette.error.main, 0.1),
                border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                borderRadius: 2,
              }}>
                <Typography variant="body2" color="error.main">{errorMessage}</Typography>
              </Paper>
            </Grid>
          )}

          {/* Empty-shell state — distinct from "no records match filters",
              which the renderer handles itself. */}
          {!isLoading && records.length === 0 && !errorMessage && (
            <Grid size={12}>
              <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                <Stack direction="column" spacing={1} alignItems="center">
                  <AssessmentIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
                  <Typography variant="body1" color="text.secondary">
                    No findings yet for <code>{auditType}</code>.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Queue a run from the Audit Center to populate this view.
                  </Typography>
                  <IconButton onClick={() => navigate('/audit')} sx={{ mt: 1 }}>
                    <AssessmentIcon />
                  </IconButton>
                </Stack>
              </Paper>
            </Grid>
          )}

          {/* Renderer */}
          {!isLoading && records.length > 0 && (
            <Grid size={12}>
              <Renderer
                records={visibleRecords}
                auditType={auditType}
                inFlight={inFlight}
                onToast={setToast}
                onRefresh={fetchRecords}
                onRequeue={handleRequeue}
              />
            </Grid>
          )}
        </Grid>
      </Container>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity || 'info'} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

export default FindingsShell;
